import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import { RouterProvider, createBrowserRouter, type RouteObject } from "react-router-dom";
import { FeaturesProvider } from "./api/features";
import { SessionProvider } from "./api/session";
import { ToastProvider } from "./components/Toast";
import { t } from "./i18n/t";

/**
 * The application shell: providers only. It owns no routes — `src/router.tsx` (task 06b) does — and it is given
 * them, so that this file compiles and its tests run before any screen exists.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // §A9.8 is polling, not caching: a stale migration state is the one thing this UI may not show.
        staleTime: 0,
        gcTime: 5 * 60_000,
        retry: false,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

/** The routes `main.tsx` falls back to when `src/router.tsx` is not in the tree yet (task 06b adds it). */
export const BOOT_ROUTES: RouteObject[] = [
  {
    path: "*",
    element: (
      <main>
        <h1>{t("common.app_name")}</h1>
        <p>{t("common.boot")}</p>
      </main>
    ),
  },
];

export function App({ routes }: { routes: RouteObject[] }): JSX.Element {
  const client = useMemo(makeQueryClient, []);
  const router = useMemo(() => createBrowserRouter(routes), [routes]);
  return (
    <QueryClientProvider client={client}>
      <FeaturesProvider>
        <SessionProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </SessionProvider>
      </FeaturesProvider>
    </QueryClientProvider>
  );
}
